/** Routes that do not require authentication */
export const PUBLIC_ROUTES = ["/login", "/gift", "/banned"] as const

/**
 * Public routes that are still valid post-login destinations.
 *
 * Most public routes, like /login and /banned, should not be redirected to
 * after a successful login. /gift is different: the route is public so a user
 * can inspect the claim page before signing in, but after sign-in they should
 * land back on the same claim route.
 */
export const POST_LOGIN_PUBLIC_ROUTES = ["/gift"] as const

const DEFAULT_POST_LOGIN_REDIRECT = "/home"
const REDIRECT_BASE_URL = "http://localhost"

/** Check if a pathname is a public (no-auth-required) route */
export function isPublicRoute(pathname: string): boolean {
  return matchesRoute(PUBLIC_ROUTES, pathname)
}

export function resolvePostLoginRedirectTarget(
  redirect: string | null
): string {
  const redirectUrl = parseSafeRelativeRedirect(redirect)

  if (redirectUrl == null) {
    return DEFAULT_POST_LOGIN_REDIRECT
  }

  if (!isAllowedPostLoginRedirectPath(redirectUrl.pathname)) {
    return DEFAULT_POST_LOGIN_REDIRECT
  }

  // Fragments can carry secrets on sensitive routes. They should never travel
  // through the login redirect query, so preserve only path and search params.
  return `${redirectUrl.pathname}${redirectUrl.search}`
}

function isAllowedPostLoginRedirectPath(pathname: string): boolean {
  return (
    !isPublicRoute(pathname) || matchesRoute(POST_LOGIN_PUBLIC_ROUTES, pathname)
  )
}

function parseSafeRelativeRedirect(redirect: string | null): URL | null {
  if (
    redirect == null ||
    !redirect.startsWith("/") ||
    redirect.startsWith("//")
  ) {
    return null
  }

  try {
    return new URL(redirect, REDIRECT_BASE_URL)
  } catch {
    return null
  }
}

function matchesRoute<T extends readonly string[]>(
  routes: T,
  pathname: string
): boolean {
  return routes.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`)
  )
}
